/**
 * Quote Cart Icon Web Component
 * Displays a quote cart icon with real-time item count in the navbar
 */
class QuoteCartIcon extends HTMLElement {
  constructor() {
    super();
    this.storageKey = 'sms_quote_cart';
    this.count = 0;
  }

  connectedCallback() {
    this.updateCount();
    this.render();
    this.setupEventListeners();

    // Listen for storage changes from other tabs
    window.addEventListener('storage', (e) => {
      if (e.key === this.storageKey) {
        this.updateCount();
        this.render();
      }
    });

    // Listen for quote-cart-updated events
    document.addEventListener('quote-cart-updated', () => {
      this.updateCount();
      this.render();
    });
  }

  updateCount() {
    try {
      const cartData = localStorage.getItem(this.storageKey);
      if (cartData) {
        const cart = JSON.parse(cartData);
        this.count = cart.items ? cart.items.reduce((sum, item) => sum + (item.quantity || 1), 0) : 0;
      } else {
        this.count = 0;
      }
    } catch (error) {
      console.error('Error reading quote cart count:', error);
      this.count = 0;
    }
  }

  render() {
    // CRITICAL: Never re-render while user is typing in form fields
    // Check if any input/textarea/select has focus
    const activeElement = document.activeElement;
    if (activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT'
    )) {
      // User is typing - defer render to avoid focus interruption
      setTimeout(() => this.render(), 150);
      return;
    }

    // Always show the icon, regardless of cart count
    this.style.display = '';
    const url = this.getAttribute('href') || '#quote-cart';
    const title = this.getAttribute('title') || 'Quote Cart';

    // Only update the count if innerHTML would change (avoid unnecessary DOM manipulation)
    const countSpan = this.querySelector('.quote-cart-icon__count');
    if (countSpan) {
      const shouldShowCount = this.count > 0;
      const currentlyVisible = countSpan.style.display !== 'none';

      // Check if we need to update
      if (countSpan.textContent === String(this.count) && shouldShowCount === currentlyVisible) {
        // Count hasn't changed and visibility is correct, no need to re-render
        return;
      }
    }

    // Show count badge only when count > 0
    const countBadge = this.count > 0 ? `<span class="quote-cart-icon__count">${this.count}</span>` : '';

    this.innerHTML = `
      <a href="${url}" class="quote-cart-icon__link focus-inset" title="${title}">
        <span class="quote-cart-icon__icon-wrapper">
          <span class="quote-cart-icon__svg">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 25 25">
              <path fill="currentColor" fill-rule="evenodd" d="M24 21h-1l-.012-11.512A.488.488 0 0 0 22.5 9H20V1.5a.488.488 0 0 0-.488-.488H9.488A.488.488 0 0 0 9 1.5V20h-.337A3.515 3.515 0 0 0 7 18.337V1.359C7 .551 6.5 0 5.664 0H1S0 0 0 1s1 1 1 1h4v16.035A3.5 3.5 0 1 0 8.663 23H24s1 0 1-1c0-1.001-1-1-1-1zm-13.001 0l-.001-10H14l.001 4.236a.488.488 0 0 0 .781.39l1.137-1.245 1.301 1.246a.488.488 0 0 0 .78-.391v-4.235L21 11v9.999L10.999 21zm1.99-15.358a.488.488 0 0 0 .758.406l.706-.797.788.796A.487.487 0 0 0 16 5.64V3h2v6h-7V3h1.988v2.642z" clip-rule="evenodd"/>
            </svg>
          </span>
          ${countBadge}
        </span>
        <span class="quote-cart-icon__text">
          <span class="quote-cart-icon__text-line">Quote</span>
          <span class="quote-cart-icon__text-line">Cart</span>
        </span>
      </a>
    `;
  }

  setupEventListeners() {
    this.addEventListener('click', (e) => {
      const link = e.target.closest('a');
      if (link && link.getAttribute('href') === '#quote-cart') {
        e.preventDefault();
        this.showQuoteCart();
      }
    });
  }

  showQuoteCart() {
    // Check if quote cart component exists
    let quoteCart = document.querySelector('quote-cart');

    if (!quoteCart) {
      // Create and show quote cart
      quoteCart = document.createElement('quote-cart');
      document.body.appendChild(quoteCart);
    }

    // Scroll to quote cart
    quoteCart.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Add a highlight effect
    quoteCart.style.transition = 'box-shadow 0.3s ease';
    quoteCart.style.boxShadow = '0 0 0 3px rgba(0, 123, 255, 0.5)';
    setTimeout(() => {
      quoteCart.style.boxShadow = '';
    }, 1000);
  }
}

// Define the custom element
if (!customElements.get('quote-cart-icon')) {
  customElements.define('quote-cart-icon', QuoteCartIcon);
}
